import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import VariationModal from '../components/VariationModal';
import './Menu.css';
import { supabase } from '../config/supabase';

const Menu = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = window.innerWidth < 768 ? 10 : 12;
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [cartItemCount, setCartItemCount] = useState(0);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({
    size: null,
    spice: null,
    addons: []
  });

  // Get cart item count
  const getCartItemCount = () => {
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        return cart.reduce((count, item) => count + item.quantity, 0);
      }
    } catch (error) {
      console.error('Error getting cart count:', error);
    }
    return 0;
  };

  // Update cart count when component mounts or cart changes
  useEffect(() => {
    const updateCartCount = () => {
      setCartItemCount(getCartItemCount());
    };
    
    // Initial load
    updateCartCount();
    
    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch menu items
  useEffect(() => {
    fetchMenuItems();
  }, [selectedCategory, sortBy]);

  // Filter items based on search
  useEffect(() => {
    let filtered = menuItems;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredItems(filtered);
  }, [menuItems, selectedCategory, searchQuery, sortBy]);

  const fetchCategories = async () => {
    try {
      // Fetch active categories from Supabase
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories
        setCategories([
          { id: 'all', name: 'All', icon: '🍽️' },
          { id: 'pizza', name: 'Pizza', icon: '🍕' },
          { id: 'burgers', name: 'Burgers', icon: '🍔' },
          { id: 'chicken', name: 'Chicken', icon: '🍗' },
          { id: 'chinese', name: 'Chinese', icon: '🥡' },
          { id: 'drinks', name: 'Drinks', icon: '🥤' },
          { id: 'desserts', name: 'Desserts', icon: '🍰' }
        ]);
        return;
      }

      if (data && data.length > 0) {
        // Format categories from Supabase
        const formatted = data.map(cat => ({
          id: cat.id,
          name: cat.name,
          icon: cat.icon || '🍽️'
        }));
        // Add "All" category at the beginning
        setCategories([{ id: 'all', name: 'All', icon: '🍽️' }, ...formatted]);
      } else {
        // If no categories exist in Supabase, use defaults
        setCategories([
          { id: 'all', name: 'All', icon: '🍽️' },
          { id: 'pizza', name: 'Pizza', icon: '🍕' },
          { id: 'burgers', name: 'Burgers', icon: '🍔' },
          { id: 'chicken', name: 'Chicken', icon: '🍗' },
          { id: 'chinese', name: 'Chinese', icon: '🥡' },
          { id: 'drinks', name: 'Drinks', icon: '🥤' },
          { id: 'desserts', name: 'Desserts', icon: '🍰' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to default categories on error
      setCategories([
        { id: 'all', name: 'All', icon: '🍽️' },
        { id: 'pizza', name: 'Pizza', icon: '🍕' },
        { id: 'burgers', name: 'Burgers', icon: '🍔' },
        { id: 'chicken', name: 'Chicken', icon: '🍗' },
        { id: 'chinese', name: 'Chinese', icon: '🥡' },
        { id: 'drinks', name: 'Drinks', icon: '🥤' },
        { id: 'desserts', name: 'Desserts', icon: '🍰' }
      ]);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('menu_items')
        .select('*')
        .eq('is_available', true);

      if (selectedCategory !== 'All') {
        query = query.eq('category', selectedCategory);
      }

      // Apply sorting
      if (sortBy === 'featured') {
        query = query.order('is_featured', { ascending: false })
          .order('rating', { ascending: false })
          .order('created_at', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformed = (data || []).map(item => {
        const customizations = item.customizations || {};
        const sizes = customizations.sizes || [];
        const spiceLevels = customizations.spiceLevels || [];
        const addons = customizations.addons || [];

        // Transform addons to ensure they have IDs
        const transformedAddons = addons.map((addon, index) => ({
          id: addon.id || `addon-${index}`,
          name: addon.name || '',
          description: addon.description || '',
          price: parseFloat(addon.price) || 0,
          icon: addon.icon || '➕'
        }));

        // Transform sizes
        const transformedSizes = sizes.map(size => ({
          name: size.name || '',
          price: parseFloat(size.price) || 0
        }));

        return {
          id: item.id,
          name: item.name,
          description: item.description || '',
          category: item.category || 'Other',
          price: parseFloat(item.price) || 0,
          originalPrice: item.original_price ? parseFloat(item.original_price) : null,
          discountPercentage: item.discount_percentage || 0,
          imageUrl: item.image_url || item.images_urls?.[0] || '',
          rating: parseFloat(item.rating) || 0,
          reviewsCount: item.reviews_count || 0,
          isVegetarian: item.is_vegetarian || false,
          isVegan: item.is_vegan || false,
          tags: item.tags || [],
          isFeatured: item.is_featured || false,
          isPopular: item.is_popular || false,
          isNew: item.is_new || false,
          isSpicy: item.tags?.some(tag => tag.toLowerCase().includes('spicy')) || false,
          sizes: transformedSizes,
          spiceLevels: spiceLevels,
          addons: transformedAddons
        };
      });

      setMenuItems(transformed);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Get paginated items
  const paginatedItems = filteredItems.slice(0, currentPage * itemsPerPage);
  const hasMore = filteredItems.length > paginatedItems.length;

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const hasVariations = (item) => {
    const hasSizes = item.sizes && item.sizes.length > 0;
    const hasSpiceLevels = item.spiceLevels && item.spiceLevels.length > 0;
    const hasAddons = item.addons && item.addons.length > 0;
    return hasSizes || hasSpiceLevels || hasAddons;
  };

  const handleAddToCartClick = (item) => {
    // Always check if item has variations - show modal if any variations exist
    const hasSizes = item.sizes && item.sizes.length > 0;
    const hasSpiceLevels = item.spiceLevels && item.spiceLevels.length > 0;
    const hasAddons = item.addons && item.addons.length > 0;
    
    if (hasSizes || hasSpiceLevels || hasAddons) {
      // Show variation modal with all available variations
      setSelectedItem(item);
      // Set default selections
      const defaultSize = hasSizes && item.sizes[0] ? item.sizes[0].name : null;
      const defaultSpice = hasSpiceLevels && item.spiceLevels[0] ? item.spiceLevels[0] : null;
      setSelectedVariations({
        size: defaultSize,
        spice: defaultSpice,
        addons: []
      });
      setShowVariationModal(true);
    } else {
      // No variations, add directly to cart
      addToCart(item);
    }
  };

  const addToCart = (item, variations = null) => {
    // Load existing cart from localStorage
    let cart = [];
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      if (savedCart) {
        cart = JSON.parse(savedCart);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }

    // Create cart item with variations if provided
    const cartItem = {
      ...item,
      quantity: 1,
      selectedSize: variations?.size || null,
      selectedSpice: variations?.spice || null,
      selectedAddons: variations?.addons || []
    };

    // Calculate price with variations
    let finalPrice = item.price;
    if (variations?.size && item.sizes) {
      const selectedSizeObj = item.sizes.find(s => s.name === variations.size);
      if (selectedSizeObj) {
        finalPrice = selectedSizeObj.price || item.price;
      }
    }
    if (variations?.addons && variations.addons.length > 0) {
      const addonsTotal = variations.addons.reduce((sum, addonId) => {
        const addon = item.addons.find(a => a.id === addonId);
        return sum + (addon ? addon.price : 0);
      }, 0);
      finalPrice += addonsTotal;
    }
    cartItem.finalPrice = finalPrice;
    cartItem.price = finalPrice;

    // Check if same item with same variations already exists
    const existingItem = cart.find(cartItem => 
      cartItem.id === item.id &&
      cartItem.selectedSize === (variations?.size || null) &&
      cartItem.selectedSpice === (variations?.spice || null) &&
      JSON.stringify(cartItem.selectedAddons?.sort()) === JSON.stringify((variations?.addons || []).sort())
    );
    
    let newCart;
    if (existingItem) {
      // Increase quantity
      newCart = cart.map(c =>
        c.id === item.id &&
        c.selectedSize === (variations?.size || null) &&
        c.selectedSpice === (variations?.spice || null) &&
        JSON.stringify(c.selectedAddons?.sort()) === JSON.stringify((variations?.addons || []).sort())
          ? { ...c, quantity: c.quantity + 1 }
          : c
      );
    } else {
      // Add new item
      newCart = [...cart, cartItem];
    }

    // Save to localStorage
    localStorage.setItem('restaurantCart', JSON.stringify(newCart));
    
    // Dispatch event to notify other components
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Show success notification
    showSuccessNotification('Item added to cart!');
  };

  const handleVariationConfirm = () => {
    if (selectedItem) {
      addToCart(selectedItem, selectedVariations);
      setShowVariationModal(false);
      setSelectedItem(null);
      setSelectedVariations({ size: null, spice: null, addons: [] });
    }
  };

  const showSuccessNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  return (
    <div className="menu-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={cartItemCount}
        onCartClick={() => navigate('/cart')}
      />

      {/* Page Header */}
      <div className="menu-page-header">
        <div className="menu-page-header-container">
          <h1 className="menu-page-title">Our Menu</h1>
          <p className="menu-page-subtitle">Discover our delicious offerings</p>
        </div>
      </div>

      {/* Category Filter Section */}
      <section className="menu-page-categories">
        <div className="menu-page-categories-container">
          <h2 className="menu-page-section-title">Categories</h2>
          <div className="menu-page-categories-scroll">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`menu-page-category-btn ${selectedCategory === category.name ? 'menu-page-category-active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedCategory(category.name);
                  setCurrentPage(1);
                }}
                onMouseDown={(e) => {
                  if (selectedCategory === category.name) {
                    e.preventDefault();
                  }
                }}
              >
                <span className="menu-page-category-icon">{category.icon}</span>
                <span className="menu-page-category-text">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="menu-page-content">
        <div className="menu-page-content-container">
          {/* Sort and Filter Bar */}
          <div className="menu-page-toolbar">
            <div className="menu-page-results">
              <span className="menu-page-results-text">
                {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} found
              </span>
            </div>
            <div className="menu-page-sort">
              <label className="menu-page-sort-label">Sort by:</label>
              <select
                className="menu-page-sort-select"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          </div>

          {/* Menu Items Grid */}
          {loading ? (
            <div className="menu-page-grid">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="menu-page-skeleton-card">
                  <div className="menu-page-skeleton-image" />
                  <div className="menu-page-skeleton-title" />
                  <div className="menu-page-skeleton-description" />
                  <div className="menu-page-skeleton-price" />
                </div>
              ))}
            </div>
          ) : paginatedItems.length > 0 ? (
            <>
              <div className="menu-page-grid">
                {paginatedItems.map((item) => (
                  <div
                    key={item.id}
                    className="menu-page-item-card"
                    onClick={() => navigate(`/item/${item.id}`)}
                  >
                    <div className="menu-page-item-image-container">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="menu-page-item-image"
                        />
                      ) : (
                        <div className="menu-page-item-image-placeholder">🍽️</div>
                      )}
                      <div className="menu-page-item-badges">
                        {item.isPopular && (
                          <span className="menu-page-item-badge menu-page-item-badge-popular">
                            Popular
                          </span>
                        )}
                        {item.isNew && (
                          <span className="menu-page-item-badge menu-page-item-badge-new">
                            New
                          </span>
                        )}
                        {item.isSpicy && (
                          <span className="menu-page-item-badge menu-page-item-badge-spicy">
                            Spicy
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="menu-page-item-details">
                      <h3 className="menu-page-item-name">{item.name}</h3>
                      <span className="menu-page-item-category">{item.category}</span>
                      <p className="menu-page-item-description">{item.description}</p>
                      <div className="menu-page-item-price-row">
                        <div className="menu-page-item-price-container">
                          {item.originalPrice && item.originalPrice > item.price && (
                            <span className="menu-page-item-price-original">
                              Rs. {item.originalPrice.toFixed(2)}
                            </span>
                          )}
                          <span className="menu-page-item-price">
                            Rs. {item.price.toFixed(2)}
                          </span>
                          {item.discountPercentage > 0 && (
                            <span className="menu-page-item-discount">
                              {item.discountPercentage}% off
                            </span>
                          )}
                        </div>
                        <button
                          className="menu-page-item-add-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCartClick(item);
                          }}
                          aria-label={`Add ${item.name} to cart`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="menu-page-load-more">
                  <button className="menu-page-load-more-btn" onClick={loadMore}>
                    Load More
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="menu-page-empty">
              <div className="menu-page-empty-icon">🍽️</div>
              <h3 className="menu-page-empty-title">No items found</h3>
              <p className="menu-page-empty-subtitle">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : 'No items available in this category'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Floating Cart Button (Mobile Only) */}
      <button
        className="floating-cart-button"
        onClick={() => navigate('/cart')}
        aria-label="Open cart"
      >
        <span className="floating-cart-icon">🛒</span>
        {cartItemCount > 0 && (
          <span className="floating-cart-badge">{cartItemCount}</span>
        )}
      </button>

      {/* Success Notification */}
      {showNotification && (
        <div className="menu-page-notification">
          <span className="menu-page-notification-icon">✓</span>
          <span className="menu-page-notification-text">{notificationMessage}</span>
        </div>
      )}

      {/* Variation Selection Modal */}
      {showVariationModal && selectedItem && (
        <VariationModal
          item={selectedItem}
          isOpen={showVariationModal}
          onClose={() => {
            setShowVariationModal(false);
            setSelectedItem(null);
            setSelectedVariations({ size: null, spice: null, addons: [] });
          }}
          onConfirm={handleVariationConfirm}
          selectedVariations={selectedVariations}
          onVariationChange={setSelectedVariations}
        />
      )}
    </div>
  );
};

export default Menu;

